import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  RadioGroup,
  Radio,
  Progress,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Spacer,
  Icon,
  Card,
  CardBody,
  Badge,
  Divider,
  CircularProgress,
  CircularProgressLabel
} from '@chakra-ui/react';
import {
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowLeft,
  FaArrowRight,
  FaFlag
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import AuthContext from '../services/AuthContext';
import axiosInstance from '../services/axiosInstance';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import Sidebar from '../shared/Sidebar';

const QCMTestPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [qcm, setQcm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const timerRef = useRef(null);
  
  const { isOpen: isSubmitOpen, onOpen: onSubmitOpen, onClose: onSubmitClose } = useDisclosure();
  const { isOpen: isTimeUpOpen, onOpen: onTimeUpOpen, onClose: onTimeUpClose } = useDisclosure();
  
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';

  useEffect(() => {
    fetchQCM();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [id]);

  useEffect(() => {
    if (qcm && qcm.timeLimit && !startTime) {
      const now = new Date();
      setStartTime(now);
      setTimeLeft(qcm.timeLimit * 60); // Convert minutes to seconds
    }
  }, [qcm]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          if (prev <= 300 && !showWarning) { // 5 minutes warning
            setShowWarning(true);
            toast({
              title: 'تنبيه',
              description: 'باقي 5 دقائق على انتهاء الوقت',
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeLeft, showWarning]);

  const fetchQCM = async () => {
    try {
      const response = await axiosInstance.get(`/qcm/tests/${id}/take`);
      setQcm(response.data);
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'فشل في تحميل الاختبار',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      navigate('/student/qcm');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionIndex, optionIndex) => {
    setAnswers({
      ...answers,
      [questionIndex]: optionIndex
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestion < qcm.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleQuestionJump = (questionIndex) => {
    setCurrentQuestion(questionIndex);
  };

  const handleTimeUp = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    onTimeUpOpen();
    setTimeout(() => {
      submitQCM(true);
    }, 3000);
  };

  const submitQCM = async (autoSubmit = false) => {
    setIsSubmitting(true);
    
    try {
      const formattedAnswers = Object.keys(answers).map(questionIndex => ({
        questionIndex: parseInt(questionIndex),
        selectedOptionIndex: answers[questionIndex]
      }));

      const timeTaken = startTime ? Math.floor((new Date() - startTime) / 1000) : 0;

      const response = await axiosInstance.post(`/qcm/attempts/${id}`, {
        answers: formattedAnswers,
        timeTaken,
        startedAt: startTime
      });

      toast({
        title: 'نجح',
        description: autoSubmit ? 'تم إرسال الاختبار تلقائياً' : 'تم إرسال الاختبار بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Navigate to results if allowed
      if (qcm.showResultsImmediately) {
        navigate(`/qcm/results/${response.data._id}`);
      } else {
        navigate('/student/qcm');
      }
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'فشل في إرسال الاختبار',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      onSubmitClose();
      onTimeUpClose();
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  const getProgressPercentage = () => {
    return (getAnsweredCount() / qcm.questions.length) * 100;
  };

  if (loading) {
    return (
      <Box dir="rtl">
        <Sidebar/>
        <Container maxW="8xl" py={8}>
          <Flex justify="center" align="center" h="400px">
            <Spinner size="xl" color={primaryColor} />
          </Flex>
        </Container>

      </Box>
    );
  }

  if (!qcm) {
    return (
      <Box dir="rtl">
        <Sidebar/>
        <Container maxW="8xl" py={8}>
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Text fontFamily="Cairo, sans-serif">الاختبار غير موجود</Text>
          </Alert>
        </Container>
     
      </Box>
    );
  }

  const currentQuestionData = qcm.questions[currentQuestion];

  return (
    <Box dir="rtl">
      <Sidebar/>
      <Container maxW="6xl" py={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <Card mb={6} bg="white" boxShadow="md">
            <CardBody>
              <Flex align="center" mb={4}>
                <VStack align="start" spacing={1}>
                  <Heading size="lg" color={secondaryColor} fontFamily="Cairo, sans-serif">
                    {qcm.title}
                  </Heading>
                  <Text color="gray.600" fontFamily="Cairo, sans-serif">
                    {qcm.course?.title}
                  </Text>
                </VStack>
                <Spacer />
                {timeLeft !== null && (
                  <VStack spacing={2}>
                    <CircularProgress 
                      value={(timeLeft / (qcm.timeLimit * 60)) * 100} 
                      color={timeLeft <= 300 ? 'red.400' : primaryColor}
                      size="80px"
                    >
                      <CircularProgressLabel fontFamily="Cairo, sans-serif" fontSize="sm">
                        {formatTime(timeLeft)}
                      </CircularProgressLabel>
                    </CircularProgress>
                    <Text fontSize="xs" color="gray.600" fontFamily="Cairo, sans-serif">
                      الوقت المتبقي
                    </Text>
                  </VStack>
                )}
              </Flex>

              <HStack justify="space-between" align="center">
                <HStack>
                  <Text fontFamily="Cairo, sans-serif">
                    السؤال {currentQuestion + 1} من {qcm.questions.length}
                  </Text>
                  <Badge colorScheme="blue" fontFamily="Cairo, sans-serif">
                    المحاولة {qcm.attemptNumber}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600" fontFamily="Cairo, sans-serif">
                  تم الإجابة على {getAnsweredCount()} من {qcm.questions.length} أسئلة
                </Text>
              </HStack>

              <Progress 
                value={getProgressPercentage()} 
                colorScheme="yellow" 
                bg="gray.200" 
                mt={3}
                borderRadius="full"
              />
            </CardBody>
          </Card>

          {/* Question Navigation */}
          <Card mb={6} bg="white" boxShadow="sm">
            <CardBody>
              <Text fontWeight="bold" mb={3} fontFamily="Cairo, sans-serif">
                الانتقال السريع للأسئلة:
              </Text>
              <Flex wrap="wrap" gap={2}>
                {qcm.questions.map((_, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant={currentQuestion === index ? 'solid' : 'outline'}
                    colorScheme={answers[index] !== undefined ? 'green' : 'gray'}
                    bg={currentQuestion === index ? primaryColor : undefined}
                    color={currentQuestion === index ? secondaryColor : undefined}
                    onClick={() => handleQuestionJump(index)}
                    fontFamily="Cairo, sans-serif"
                  >
                    {index + 1}
                  </Button>
                ))}
              </Flex>
            </CardBody>
          </Card>

          {/* Current Question */}
          <Card mb={6} bg="white" boxShadow="md">
            <CardBody>
              <VStack align="stretch" spacing={6}>
                <Box>
                  <HStack mb={4}>
                    <Icon as={FaFlag} color={primaryColor} />
                    <Text fontWeight="bold" fontSize="lg" fontFamily="Cairo, sans-serif">
                      السؤال {currentQuestion + 1}
                    </Text>
                  </HStack>
                  <Text fontSize="lg" lineHeight="1.8" fontFamily="Cairo, sans-serif">
                    {currentQuestionData.questionText}
                  </Text>
                </Box>

                <Divider />

                <RadioGroup
                  value={answers[currentQuestion]?.toString() || ''}
                  onChange={(value) => handleAnswerChange(currentQuestion, parseInt(value))}
                >
                  <VStack align="stretch" spacing={4}>
                    {currentQuestionData.options.map((option, optionIndex) => (
                      <motion.div
                        key={optionIndex}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Box
                          p={4}
                          border="2px"
                          borderColor={answers[currentQuestion] === optionIndex ? primaryColor : 'gray.200'}
                          borderRadius="lg"
                          bg={answers[currentQuestion] === optionIndex ? `${primaryColor}10` : 'white'}
                          cursor="pointer"
                          transition="all 0.3s"
                          _hover={{ borderColor: primaryColor, bg: `${primaryColor}05` }}
                          onClick={() => handleAnswerChange(currentQuestion, optionIndex)}
                        >
                          <Radio value={optionIndex.toString()} colorScheme="yellow">
                            <Text fontFamily="Cairo, sans-serif" mr={3}>
                              {option.text}
                            </Text>
                          </Radio>
                        </Box>
                      </motion.div>
                    ))}
                  </VStack>
                </RadioGroup>
              </VStack>
            </CardBody>
          </Card>

          {/* Navigation Buttons */}
          <Card bg="white" boxShadow="md">
            <CardBody>
              <Flex justify="space-between" align="center">
                <Button
                  leftIcon={<FaArrowRight />}
                  onClick={handlePreviousQuestion}
                  isDisabled={currentQuestion === 0}
                  variant="outline"
                  fontFamily="Cairo, sans-serif"
                >
                  السؤال السابق
                </Button>

                <HStack spacing={4}>
                  <Button
                    colorScheme="red"
                    variant="outline"
                    onClick={onSubmitOpen}
                    fontFamily="Cairo, sans-serif"
                  >
                    إنهاء الاختبار
                  </Button>
                  
                  {currentQuestion < qcm.questions.length - 1 ? (
                    <Button
                      rightIcon={<FaArrowLeft />}
                      onClick={handleNextQuestion}
                      colorScheme="yellow"
                      bg={primaryColor}
                      color={secondaryColor}
                      fontFamily="Cairo, sans-serif"
                    >
                      السؤال التالي
                    </Button>
                  ) : (
                    <Button
                      rightIcon={<FaCheckCircle />}
                      onClick={onSubmitOpen}
                      colorScheme="green"
                      fontFamily="Cairo, sans-serif"
                    >
                      إرسال الاختبار
                    </Button>
                  )}
                </HStack>
              </Flex>
            </CardBody>
          </Card>
        </motion.div>

        {/* Submit Confirmation Modal */}
        <Modal isOpen={isSubmitOpen} onClose={onSubmitClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontFamily="Cairo, sans-serif">تأكيد إرسال الاختبار</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <Icon as={FaExclamationTriangle} color="orange.500" boxSize={12} />
                <Text textAlign="center" fontFamily="Cairo, sans-serif">
                  هل أنت متأكد من إرسال الاختبار؟
                </Text>
                <Text fontSize="sm" color="gray.600" textAlign="center" fontFamily="Cairo, sans-serif">
                  تم الإجابة على {getAnsweredCount()} من {qcm.questions.length} أسئلة
                </Text>
                {getAnsweredCount() < qcm.questions.length && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm" fontFamily="Cairo, sans-serif">
                      لم تجب على جميع الأسئلة. الأسئلة غير المجاب عليها ستحسب خطأ.
                    </Text>
                  </Alert>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onSubmitClose} fontFamily="Cairo, sans-serif">
                إلغاء
              </Button>
              <Button
                colorScheme="green"
                onClick={() => submitQCM(false)}
                isLoading={isSubmitting}
                fontFamily="Cairo, sans-serif"
              >
                تأكيد الإرسال
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Time Up Modal */}
        <Modal isOpen={isTimeUpOpen} onClose={() => {}} isCentered closeOnOverlayClick={false}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontFamily="Cairo, sans-serif">انتهى الوقت</ModalHeader>
            <ModalBody>
              <VStack spacing={4}>
                <Icon as={FaClock} color="red.500" boxSize={12} />
                <Text textAlign="center" fontFamily="Cairo, sans-serif">
                  انتهى الوقت المخصص للاختبار
                </Text>
                <Text fontSize="sm" color="gray.600" textAlign="center" fontFamily="Cairo, sans-serif">
                  سيتم إرسال إجاباتك تلقائياً...
                </Text>
                <Spinner color={primaryColor} />
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
     
    </Box>
  );
};

export default QCMTestPage;

